// src/components/WorkflowCreator/nodes/nodeDefinitions.js
// Definiciones de todos los tipos de nodos disponibles para workflows

// Simple UUID generator (crypto.randomUUID fallback)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Categorías de nodos para el panel lateral
 */
export const NODE_CATEGORIES = {
  TRIGGERS: {
    id: 'triggers',
    label: 'Triggers',
    description: 'Events that start the workflow',
    color: 'from-green-500 to-emerald-600',
    icon: 'Zap'
  },
  AI: {
    id: 'ai',
    label: 'AI & Agents',
    description: 'AI-powered processing steps',
    color: 'from-purple-500 to-violet-600',
    icon: 'Bot'
  },
  INTEGRATIONS: {
    id: 'integrations',
    label: 'Integrations',
    description: 'External service connections',
    color: 'from-blue-500 to-cyan-600',
    icon: 'Plug'
  },
  NOTIFICATIONS: {
    id: 'notifications',
    label: 'Notifications',
    description: 'Send messages and alerts',
    color: 'from-orange-500 to-amber-600',
    icon: 'Bell'
  },
  DATA: {
    id: 'data',
    label: 'Data Processing',
    description: 'Transform and query data',
    color: 'from-teal-500 to-cyan-600',
    icon: 'Database'
  },
  CONTROL: {
    id: 'control',
    label: 'Control Flow',
    description: 'Logic and flow control',
    color: 'from-pink-500 to-rose-600',
    icon: 'GitBranch'
  },
  SYSTEM: {
    id: 'system',
    label: 'System',
    description: 'Logging and metrics',
    color: 'from-gray-500 to-slate-600',
    icon: 'Settings'
  }
};

/**
 * Definiciones de todos los tipos de nodos
 * Cada nodo incluye:
 * - Metadata para UI (icon, label, category, etc)
 * - Schema de parámetros requeridos/opcionales
 * - Función para generar datos iniciales
 */
export const NODE_DEFINITIONS = {
  // ============================================================================
  // TRIGGERS
  // ============================================================================
  shopify_trigger: {
    type: 'shopify_trigger',
    category: 'triggers',
    label: 'Shopify Trigger',
    description: 'Triggered by Shopify webhooks (orders, products, customers)',
    icon: 'ShoppingBag',
    color: 'from-green-500 to-emerald-600',
    isTrigger: true,
    parameters: {
      required: ['event_type', 'shop_domain'],
      optional: [],
      schema: {
        event_type: {
          type: 'select',
          label: 'Event Type',
          options: [
            { value: 'orders/create', label: 'Order Created' },
            { value: 'orders/updated', label: 'Order Updated' },
            { value: 'orders/paid', label: 'Order Paid' },
            { value: 'orders/fulfilled', label: 'Order Fulfilled' },
            { value: 'orders/cancelled', label: 'Order Cancelled' },
            { value: 'products/create', label: 'Product Created' },
            { value: 'products/update', label: 'Product Updated' },
            { value: 'customers/create', label: 'Customer Created' },
            { value: 'customers/update', label: 'Customer Updated' },
          ],
          default: 'orders/create'
        },
        shop_domain: {
          type: 'text',
          label: 'Shop Domain',
          placeholder: 'your-store.myshopify.com',
          required: true
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'shopify',
      configuration: {
        enabled: true,
        description: `Shopify ${params.event_type} trigger`,
        metadata: {},
        trigger_type: 'shopify',
        shop_domain: params.shop_domain,
        event_type: params.event_type,
        integration_id: params.integration_id || null
      }
    })
  },

  webhook_trigger: {
    type: 'webhook_trigger',
    category: 'triggers',
    label: 'Webhook Trigger',
    description: 'Triggered by incoming webhooks',
    icon: 'Webhook',
    color: 'from-blue-500 to-indigo-600',
    isTrigger: true,
    parameters: {
      required: [],
      optional: ['webhook_path', 'method'],
      schema: {
        webhook_path: {
          type: 'text',
          label: 'Webhook Path',
          placeholder: '/my-webhook'
        },
        method: {
          type: 'select',
          label: 'HTTP Method',
          options: [
            { value: 'POST', label: 'POST' },
            { value: 'GET', label: 'GET' },
            { value: 'PUT', label: 'PUT' }
          ],
          default: 'POST'
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'webhook',
      configuration: {
        enabled: true,
        webhook_path: params.webhook_path,
        method: params.method || 'POST'
      }
    })
  },

  schedule_trigger: {
    type: 'schedule_trigger',
    category: 'triggers',
    label: 'Schedule Trigger',
    description: 'Triggered on a schedule (cron)',
    icon: 'Clock',
    color: 'from-yellow-500 to-orange-600',
    isTrigger: true,
    parameters: {
      required: ['cron_expression'],
      optional: ['timezone', 'start_date', 'end_date', 'max_executions', 'skip_if_running', 'execution_timeout_minutes', 'description'],
      schema: {
        cron_expression: {
          type: 'schedule',
          label: 'Schedule',
          required: true
        },
        timezone: {
          type: 'text',
          label: 'Timezone',
          placeholder: 'America/New_York',
          default: 'UTC'
        },
        start_date: {
          type: 'text',
          label: 'Start Date (optional)',
          placeholder: '2026-05-01T00:00:00Z'
        },
        end_date: {
          type: 'text',
          label: 'End Date (optional)',
          placeholder: '2026-12-31T23:59:59Z'
        },
        max_executions: {
          type: 'number',
          label: 'Max Executions (optional)',
          placeholder: 'Leave empty for unlimited',
          min: 1
        },
        skip_if_running: {
          type: 'select',
          label: 'Skip if Running',
          default: 'true',
          options: [
            { value: 'true', label: 'Yes - Skip if already running' },
            { value: 'false', label: 'No - Allow concurrent runs' }
          ]
        },
        execution_timeout_minutes: {
          type: 'number',
          label: 'Execution Timeout (minutes, optional)',
          placeholder: 'Leave empty for default',
          min: 1
        },
        description: {
          type: 'textarea',
          label: 'Description (optional)',
          placeholder: 'Describe what this schedule does',
          rows: 2
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'scheduled',
      configuration: {
        trigger_type: 'scheduled',
        enabled: true,
        cron_expression: params.cron_expression,
        timezone: params.timezone || 'UTC',
        ...(params.start_date && { start_date: params.start_date }),
        ...(params.end_date && { end_date: params.end_date }),
        ...(params.max_executions && { max_executions: parseInt(params.max_executions, 10) }),
        skip_if_running: params.skip_if_running === 'false' ? false : true,
        ...(params.execution_timeout_minutes && { execution_timeout_minutes: parseInt(params.execution_timeout_minutes, 10) }),
        ...(params.description && { description: params.description }),
        metadata: {}
      }
    })
  },

  whatsapp_trigger: {
    type: 'whatsapp_trigger',
    category: 'triggers',
    label: 'WhatsApp Trigger',
    description: 'Triggered by WhatsApp messages and events',
    icon: 'MessageCircle',
    color: 'from-green-500 to-emerald-600',
    isTrigger: true,
    parameters: {
      required: ['integration_id', 'business_phone_number_id', 'event_types'],
      optional: [],
      schema: {
        integration_id: {
          type: 'whatsapp_integration_select',
          label: 'WhatsApp Integration',
          required: true,
          description: 'Select the WhatsApp Business account to listen to'
        },
        business_phone_number_id: {
          type: 'text',
          label: 'Phone Number ID',
          placeholder: '387311654443856',
          required: true,
          description: 'WhatsApp Business Phone Number ID from Meta'
        },
        event_types: {
          type: 'multi_select',
          label: 'Event Types',
          required: true,
          options: [
            { value: 'messages', label: 'Messages (Received)', default: true },
            { value: 'message_deliveries', label: 'Message Deliveries' },
            { value: 'message_reads', label: 'Message Reads' },
            { value: 'message_reactions', label: 'Message Reactions' },
            { value: 'contacts', label: 'Contacts Update' },
            { value: 'account_update', label: 'Account Update' },
            { value: 'phone_number_name_update', label: 'Phone Number Name Update' },
            { value: 'phone_number_quality_update', label: 'Phone Number Quality Update' }
          ],
          default: ['messages'],
          description: 'Types of WhatsApp events to trigger this workflow'
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'whatsapp',
      configuration: {
        enabled: true,
        description: 'WhatsApp trigger for selected events',
        trigger_type: 'whatsapp',
        integration_type: 'whatsapp',
        path: '/whatsapp/webhook',
        method: 'POST',
        content_type: 'application/json',
        business_phone_number_id: params.business_phone_number_id,
        event_types: params.event_types || ['messages'],
        integration_id: params.integration_id,
        authentication_type: 'webhook_signature'
      }
    })
  },

  instagram_trigger: {
    type: 'instagram_trigger',
    category: 'triggers',
    label: 'Instagram Trigger',
    description: 'Triggered by Instagram Messaging events (DMs, reactions, etc.) or public comment events',
    icon: 'Instagram',
    color: 'from-pink-500 to-fuchsia-600',
    isTrigger: true,
    parameters: {
      required: ['integration_id', 'instagram_business_account_id'],
      optional: ['event_types'],
      schema: {
        integration_id: {
          type: 'instagram_integration_select',
          label: 'Instagram Integration',
          required: true,
          description: 'Select the Meta integration (Instagram Business account) to listen to'
        },
        instagram_business_account_id: {
          type: 'text',
          label: 'Instagram Business Account ID',
          placeholder: '17841416557284103',
          required: true,
          description: 'Instagram Business Account ID from Meta Graph API'
        },
        event_types: {
          type: 'multi_select',
          label: 'Event Types',
          options: [
            { value: 'messages', label: 'Messages (Incoming DMs)', default: true },
            { value: 'comments', label: 'Comments (Public post/reel comments)' },
            { value: 'messaging_postbacks', label: 'Postbacks (Button/Icebreaker taps)' },
            { value: 'message_reactions', label: 'Message Reactions' },
            { value: 'messaging_seen', label: 'Message Seen' },
            { value: 'messaging_referral', label: 'Referrals' }
          ],
          default: ['messages'],
          description: 'Instagram event types to handle'
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'instagram_webhook',
      configuration: {
        enabled: true,
        description: 'Instagram trigger for selected events',
        trigger_type: 'instagram_webhook',
        integration_type: 'instagram',
        path: '/webhooks/instagram',
        method: 'POST',
        content_type: 'application/json',
        instagram_business_account_id: params.instagram_business_account_id,
        event_types: params.event_types || ['messages'],
        integration_id: params.integration_id,
        authentication_type: 'webhook_signature'
      }
    })
  },

  executable_by_workflow_trigger: {
    type: 'executable_by_workflow_trigger',
    category: 'triggers',
    label: 'Callable by Workflow',
    description: 'This workflow can be called by another workflow',
    icon: 'Shuffle',
    color: 'from-indigo-500 to-violet-600',
    isTrigger: true,
    parameters: {
      required: [],
      optional: ['name', 'description', 'inputs'],
      schema: {
        name: {
          type: 'text',
          label: 'Display Name',
          placeholder: 'e.g. Send Welcome Email'
        },
        description: {
          type: 'textarea',
          label: 'Description',
          placeholder: 'What does this workflow do?',
          rows: 2
        },
        inputs: {
          type: 'array',
          label: 'Input Parameters',
          itemLabel: 'Input',
          itemSchema: {
            name: { type: 'text', label: 'Name', placeholder: 'customer_id', required: true },
            type: {
              type: 'select',
              label: 'Type',
              default: 'string',
              options: [
                { value: 'string', label: 'string' },
                { value: 'number', label: 'number' },
                { value: 'boolean', label: 'boolean' },
                { value: 'object', label: 'object' },
                { value: 'array', label: 'array' },
              ]
            },
            required: { type: 'select', label: 'Required', default: 'false', options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }] },
            description: { type: 'text', label: 'Description', placeholder: 'What this input is used for' }
          }
        }
      }
    },
    generateTriggerConfig: (params) => ({
      trigger_type: 'executable_by_workflow',
      configuration: {
        trigger_type: 'executable_by_workflow',
        enabled: true,
        ...(params.name && { name: params.name }),
        ...(params.description && { description: params.description }),
        inputs: params.inputs || [],
        metadata: {}
      }
    })
  },

  // ============================================================================
  // AI STEPS
  // ============================================================================
  assistant_task: {
    type: 'assistant_task',
    category: 'ai',
    label: 'AI Agent Task',
    description: 'Process with an AI agent',
    icon: 'Bot',
    color: 'from-purple-500 to-violet-600',
    parameters: {
      required: ['assistant_id', 'message_template'],
      optional: ['chat_mode', 'temperature', 'max_tokens', 'context_variables'],
      schema: {
        assistant_id: {
          type: 'assistant_select',
          label: 'Agent',
          required: true
        },
        message_template: {
          type: 'textarea',
          label: 'Message Template',
          placeholder: 'Analyze this order: {{input:webhook_payload}}',
          required: true,
          rows: 4
        },
        chat_mode: {
          type: 'select',
          label: 'Chat Mode',
          options: [
            { value: 'one_shot', label: 'One Shot (single response)' },
            { value: 'conversation', label: 'Conversation (maintains context)' }
          ],
          default: 'one_shot'
        },
        temperature: {
          type: 'number',
          label: 'Temperature',
          min: 0,
          max: 2,
          step: 0.1,
          default: 0.7
        },
        max_tokens: {
          type: 'number',
          label: 'Max Tokens',
          min: 1,
          max: 4000,
          default: 2000
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'assistant_task',
      assistant_id: params.assistant_id,
      message_template: params.message_template,
      chat_mode: params.chat_mode || 'one_shot',
      temperature: params.temperature ?? 0.7,
      max_tokens: params.max_tokens ?? 2000
    })
  },

  llm_call: {
    type: 'llm_call',
    category: 'ai',
    label: 'Direct LLM Call',
    description: 'Call an LLM directly without agent',
    icon: 'Sparkles',
    color: 'from-violet-500 to-purple-600',
    parameters: {
      required: ['llm_id', 'user_message'],
      optional: ['system_prompt', 'temperature', 'max_tokens'],
      schema: {
        llm_id: {
          type: 'llm_select',
          label: 'Model',
          required: true
        },
        user_message: {
          type: 'textarea',
          label: 'Message',
          placeholder: 'Your prompt here... e.g. Classify this message: {{trigger:message}}',
          required: true,
          rows: 4
        },
        system_prompt: {
          type: 'textarea',
          label: 'System Prompt',
          placeholder: 'Optional system instructions (role, output format)...',
          rows: 2
        },
        temperature: {
          type: 'number',
          label: 'Temperature',
          min: 0,
          max: 2,
          step: 0.1
        },
        max_tokens: {
          type: 'number',
          label: 'Max Tokens',
          min: 1,
          max: 4000
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'llm_call',
      llm_id: params.llm_id,
      user_message: params.user_message,
      system_prompt: params.system_prompt || null,
      temperature: params.temperature ?? null,
      max_tokens: params.max_tokens ?? null
    })
  },

  // ============================================================================
  // INTEGRATIONS
  // ============================================================================
  shopify_update_order: {
    type: 'shopify_update_order',
    category: 'integrations',
    label: 'Update Shopify Order',
    description: 'Update an order in Shopify',
    icon: 'ShoppingBag',
    color: 'from-green-500 to-emerald-600',
    parameters: {
      required: ['order_id'],
      optional: ['shop_domain', 'tags', 'note', 'email', 'phone'],
      schema: {
        order_id: {
          type: 'text',
          label: 'Order ID',
          placeholder: '{{input:webhook_payload.id}}',
          required: true
        },
        shop_domain: {
          type: 'text',
          label: 'Shop Domain',
          placeholder: 'Auto-detected if not provided'
        },
        tags: {
          type: 'text',
          label: 'Tags',
          placeholder: '{{context:step_name.result_data.tags}} or comma-separated'
        },
        note: {
          type: 'textarea',
          label: 'Order Note',
          placeholder: 'Note to add to the order',
          rows: 2
        },
        email: {
          type: 'text',
          label: 'Customer Email',
          placeholder: 'Optional email update'
        },
        phone: {
          type: 'text',
          label: 'Customer Phone',
          placeholder: 'Optional phone update'
        }
      },
      advanced: {
        accepts_marketing: {
          type: 'select',
          label: 'Accepts Marketing',
          options: [
            { value: '', label: '— Not changed —' },
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' }
          ],
          description: 'Whether the customer accepts marketing emails'
        },
        shipping_address: {
          type: 'json',
          label: 'Shipping Address',
          placeholder: '{"first_name": "John", "address1": "123 Main St", "city": "NYC", "country_code": "US"}',
          rows: 3,
          description: 'Override shipping address'
        },
        billing_address: {
          type: 'json',
          label: 'Billing Address',
          placeholder: '{"first_name": "John", "address1": "123 Main St", "city": "NYC", "country_code": "US"}',
          rows: 3,
          description: 'Override billing address'
        },
        metafields: {
          type: 'json',
          label: 'Metafields',
          placeholder: '[{"namespace": "custom", "key": "status", "value": "vip", "type": "single_line_text_field"}]',
          rows: 3,
          description: 'Array of Shopify metafield objects'
        }
      }
    },
    generateStepParams: (params) => {
      let shipping_address = null;
      let billing_address = null;
      let metafields = null;
      try { shipping_address = params.shipping_address ? JSON.parse(params.shipping_address) : null; } catch (e) {}
      try { billing_address = params.billing_address ? JSON.parse(params.billing_address) : null; } catch (e) {}
      try { metafields = params.metafields ? JSON.parse(params.metafields) : null; } catch (e) {}

      const result = {
        step_type: 'shopify_update_order',
        order_id: params.order_id,
        shop_domain: params.shop_domain || null,
        tags: params.tags || null,
        note: params.note || null,
        email: params.email || null,
        phone: params.phone || null,
        use_template_variables: true
      };
      if (params.accepts_marketing !== '' && params.accepts_marketing != null) {
        result.accepts_marketing = params.accepts_marketing === 'true';
      }
      if (shipping_address) result.shipping_address = shipping_address;
      if (billing_address) result.billing_address = billing_address;
      if (metafields) result.metafields = metafields;
      return result;
    }
  },

  api_call: {
    type: 'api_call',
    category: 'integrations',
    label: 'API Call',
    description: 'Make an HTTP request to external API',
    icon: 'Globe',
    color: 'from-blue-500 to-cyan-600',
    parameters: {
      required: ['url'],
      optional: ['method', 'headers', 'body', 'timeout'],
      schema: {
        url: {
          type: 'text',
          label: 'URL',
          placeholder: 'https://api.example.com/endpoint',
          required: true
        },
        method: {
          type: 'select',
          label: 'Method',
          options: [
            { value: 'GET', label: 'GET' },
            { value: 'POST', label: 'POST' },
            { value: 'PUT', label: 'PUT' },
            { value: 'PATCH', label: 'PATCH' },
            { value: 'DELETE', label: 'DELETE' }
          ],
          default: 'GET'
        },
        headers: {
          type: 'json',
          label: 'Headers',
          placeholder: '{"Authorization": "Bearer xxx"}',
          rows: 3
        },
        body: {
          type: 'json',
          label: 'Body',
          placeholder: '{"key": "value"}',
          rows: 4
        },
        timeout: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 1,
          max: 300,
          default: 30
        }
      },
      advanced: {
        credential_id: {
          type: 'text',
          label: 'Credential ID',
          placeholder: 'API credential UUID',
          description: 'Credential for authenticated APIs'
        },
        query_params: {
          type: 'json',
          label: 'Query Parameters',
          placeholder: '{"page": "1", "limit": "10"}',
          rows: 3,
          description: 'URL query parameters appended to the URL'
        },
        body_type: {
          type: 'select',
          label: 'Body Type',
          options: [
            { value: 'json', label: 'JSON' },
            { value: 'form', label: 'Form Data' },
            { value: 'raw', label: 'Raw' },
            { value: 'none', label: 'None' }
          ],
          default: 'json',
          description: 'Format of the request body'
        },
        response_type: {
          type: 'select',
          label: 'Response Type',
          options: [
            { value: 'json', label: 'JSON' },
            { value: 'text', label: 'Text' },
            { value: 'binary', label: 'Binary' }
          ],
          default: 'json',
          description: 'Expected format of the API response'
        }
      }
    },
    generateStepParams: (params) => {
      let headers = null;
      let body = null;
      try {
        headers = params.headers ? JSON.parse(params.headers) : null;
      } catch (e) {}
      try {
        body = params.body ? JSON.parse(params.body) : null;
      } catch (e) {}
      return {
        step_type: 'api_call',
        url: params.url,
        method: params.method || 'GET',
        credential_id: params.credential_id || null,
        headers,
        query_params: params.query_params || null,
        body,
        body_type: params.body_type || 'json',
        timeout: params.timeout ?? 30,
        response_type: params.response_type || 'json'
      };
    }
  },

  webhook: {
    type: 'webhook',
    category: 'integrations',
    label: 'Send Webhook',
    description: 'Send data to a webhook URL',
    icon: 'Send',
    color: 'from-indigo-500 to-blue-600',
    parameters: {
      required: ['url'],
      optional: ['method', 'headers', 'payload_template'],
      schema: {
        url: {
          type: 'text',
          label: 'Webhook URL',
          placeholder: 'https://hooks.example.com/webhook',
          required: true
        },
        method: {
          type: 'select',
          label: 'Method',
          options: [
            { value: 'POST', label: 'POST' },
            { value: 'PUT', label: 'PUT' }
          ],
          default: 'POST'
        },
        headers: {
          type: 'json',
          label: 'Headers',
          placeholder: '{"Content-Type": "application/json"}'
        },
        payload_template: {
          type: 'textarea',
          label: 'Payload Template',
          placeholder: 'Template with {{context:step_name.result}}',
          rows: 4
        }
      }
    },
    generateStepParams: (params) => {
      let headers = null;
      try {
        headers = params.headers ? JSON.parse(params.headers) : null;
      } catch (e) {}
      return {
        step_type: 'webhook',
        url: params.url,
        method: params.method || 'POST',
        headers,
        payload_template: params.payload_template || null
      };
    }
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================
  whatsapp_notification: {
    type: 'whatsapp_notification',
    category: 'notifications',
    label: 'WhatsApp Message',
    description: 'Send a WhatsApp message',
    icon: 'MessageCircle',
    color: 'from-green-500 to-emerald-600',
    parameters: {
      required: ['phone_number_id', 'recipient', 'message_content'],
      optional: ['integration_id', 'message_type', 'template_name', 'template_language', 'template_parameters', 'header_image_url', 'template_button_parameters'],
      schema: {
        integration_id: {
          type: 'integration_select',
          label: 'WhatsApp Integration (Optional)',
          integration_type: 'meta',
          required: false
        },
        phone_number_id: {
          type: 'text',
          label: 'Phone Number ID',
          placeholder: 'WhatsApp Business Phone Number ID',
          required: true
        },
        recipient: {
          type: 'text',
          label: 'Recipient',
          placeholder: '+1234567890 or {{input:webhook_payload.customer.phone}}',
          required: true
        },
        message_type: {
          type: 'select',
          label: 'Message Type',
          options: [
            { value: 'text', label: 'Text Message' },
            { value: 'template', label: 'Template Message' },
            { value: 'image', label: 'Image' },
            { value: 'document', label: 'Document' }
          ],
          default: 'text'
        },
        message_content: {
          type: 'textarea',
          label: 'Message Content',
          placeholder: 'Hello {{input:webhook_payload.customer.first_name}}!',
          required: true,
          rows: 3
        },
        template_name: {
          type: 'text',
          label: 'Template Name',
          placeholder: 'order_confirmation (required for template type)'
        },
        template_language: {
          type: 'text',
          label: 'Template Language',
          placeholder: 'en',
          default: 'en'
        },
        template_parameters: {
          type: 'json',
          label: 'Template Parameters',
          placeholder: '[{"name": "customer_name", "value": "{{input:...}}"}]',
          rows: 3
        },
        header_image_url: {
          type: 'text',
          label: 'Header Image URL',
          placeholder: 'https://example.com/banner.jpg or {{input:...}}',
          description: 'Imagen del header de la plantilla (solo si la plantilla tiene header de tipo imagen).',
          showWhen: { field: 'message_type', equals: 'template' }
        },
        template_button_parameters: {
          type: 'whatsapp_button_url',
          label: 'Dynamic URL Button',
          placeholder: 'order/12345 or {{input:webhook_payload.order_id}}',
          description: 'Valor de la variable del botón URL: solo el sufijo dinámico que reemplaza el {{1}} del botón (NO la URL completa). Meta permite una sola variable, al final de la URL.',
          showWhen: { field: 'message_type', equals: 'template' }
        }
      },
      advanced: {
        media_url: {
          type: 'text',
          label: 'Media URL',
          placeholder: 'https://example.com/image.jpg',
          description: 'URL of media to send (for image/document message types)'
        },
        caption: {
          type: 'text',
          label: 'Caption',
          placeholder: 'Image caption text',
          description: 'Caption for image or document messages'
        },
        filename: {
          type: 'text',
          label: 'Filename',
          placeholder: 'document.pdf',
          description: 'Filename for document messages'
        }
      }
    },
    generateStepParams: (params) => {
      let parsedTemplateParams = null;
      if (params.template_parameters) {
        try {
          parsedTemplateParams = JSON.parse(params.template_parameters);
        } catch (e) {
          console.error('Invalid JSON in template_parameters:', e);
          parsedTemplateParams = null;
        }
      }

      // Botón URL dinámico. El usuario solo escribe el sufijo (reemplaza {{1}} del botón).
      // El backend (whatsapp_notification v3) espera un array amigable de objetos
      // { sub_type, index, value }; sub_type e index tienen defaults ('url' / '0').
      // El valor puede llegar como string (editado en la UI) o como array (passthrough
      // de un step cargado del backend sin tocar el campo).
      let templateButtonParameters = null;
      const rawButton = params.template_button_parameters;
      if (Array.isArray(rawButton)) {
        templateButtonParameters = rawButton.length > 0 ? rawButton : null;
      } else if (typeof rawButton === 'string' && rawButton.trim()) {
        templateButtonParameters = [
          { sub_type: 'url', index: '0', value: rawButton.trim() }
        ];
      }

      return {
        step_type: 'whatsapp_notification',
        integration_id: params.integration_id || null,
        phone_number_id: params.phone_number_id,
        recipient: params.recipient,
        message_content: params.message_content,
        message_type: params.message_type || 'text',
        template_name: params.template_name || null,
        template_language: params.template_language || 'en',
        template_parameters: parsedTemplateParams,
        header_image_url: params.header_image_url || null,
        template_button_parameters: templateButtonParameters,
        media_url: params.media_url || null,
        caption: params.caption || null,
        filename: params.filename || null
      };
    }
  },

  instagram_comment_reply: {
    type: 'instagram_comment_reply',
    category: 'notifications',
    label: 'Instagram Comment Reply',
    description: 'Publicly reply to a comment on an Instagram post via the connected Business account',
    icon: 'Instagram',
    color: 'from-pink-500 to-fuchsia-600',
    parameters: {
      required: ['comment_id', 'message'],
      optional: ['integration_id', 'instagram_business_account_id'],
      schema: {
        integration_id: {
          type: 'instagram_integration_select',
          label: 'Instagram Integration',
          required: false,
          description: 'Select the Meta integration (Instagram Business account) to reply from'
        },
        instagram_business_account_id: {
          type: 'text',
          label: 'Instagram Business Account ID',
          placeholder: '17841416557284103',
          required: false,
          description: 'Only needed if not selecting an integration above'
        },
        comment_id: {
          type: 'text',
          label: 'Comment ID',
          placeholder: '{{trigger:entry.0.changes.0.value.id}}',
          required: true,
          description: 'ID of the Instagram comment to reply to, from the comments webhook event'
        },
        message: {
          type: 'textarea',
          label: 'Reply Message',
          placeholder: 'Thanks for your comment! {{context:responder_con_ai.response}}',
          required: true,
          rows: 3,
          description: 'Public reply text (max 2200 characters, Instagram\'s comment limit)'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'instagram_comment_reply',
      integration_id: params.integration_id || null,
      instagram_business_account_id: params.instagram_business_account_id || null,
      comment_id: params.comment_id,
      message: params.message
    })
  },

  slack_notification: {
    type: 'slack_notification',
    category: 'notifications',
    label: 'Slack Message',
    description: 'Send a Slack message',
    icon: 'Hash',
    color: 'from-purple-500 to-fuchsia-600',
    parameters: {
      required: ['channel', 'text'],
      optional: ['thread_ts', 'blocks'],
      schema: {
        channel: {
          type: 'text',
          label: 'Channel',
          placeholder: '#general or channel ID',
          required: true
        },
        text: {
          type: 'textarea',
          label: 'Message Text',
          placeholder: 'Your message here...',
          required: true,
          rows: 3
        },
        thread_ts: {
          type: 'text',
          label: 'Thread Timestamp',
          placeholder: 'Optional - for thread replies'
        },
        blocks: {
          type: 'json',
          label: 'Block Kit Blocks',
          placeholder: 'Optional Slack Block Kit JSON',
          rows: 4
        }
      }
    },
    generateStepParams: (params) => {
      let blocks = null;
      try {
        blocks = params.blocks ? JSON.parse(params.blocks) : null;
      } catch (e) {}
      return {
        step_type: 'slack_notification',
        channel: params.channel,
        text: params.text,
        thread_ts: params.thread_ts || null,
        blocks,
        unfurl_links: true,
        unfurl_media: true
      };
    }
  },

  discord_notification: {
    type: 'discord_notification',
    category: 'notifications',
    label: 'Discord Message',
    description: 'Send a Discord message',
    icon: 'MessageSquare',
    color: 'from-indigo-500 to-violet-600',
    parameters: {
      required: ['channel_id'],
      optional: ['content', 'embeds', 'username', 'avatar_url'],
      schema: {
        channel_id: {
          type: 'text',
          label: 'Channel ID',
          placeholder: 'Discord channel ID',
          required: true
        },
        content: {
          type: 'textarea',
          label: 'Message Content',
          placeholder: 'Your message here...',
          rows: 3
        },
        embeds: {
          type: 'json',
          label: 'Embeds',
          placeholder: 'Discord embed JSON array',
          rows: 4
        },
        username: {
          type: 'text',
          label: 'Bot Username Override',
          placeholder: 'Optional custom username'
        },
        avatar_url: {
          type: 'text',
          label: 'Avatar URL Override',
          placeholder: 'Optional custom avatar'
        }
      }
    },
    generateStepParams: (params) => {
      let embeds = null;
      try {
        embeds = params.embeds ? JSON.parse(params.embeds) : null;
      } catch (e) {}
      return {
        step_type: 'discord_notification',
        channel_id: params.channel_id,
        content: params.content || null,
        embeds,
        username: params.username || null,
        avatar_url: params.avatar_url || null,
        tts: false
      };
    }
  },

  gmail: {
    type: 'gmail',
    category: 'notifications',
    label: 'Send Email (Gmail)',
    description: 'Send an email via Gmail',
    icon: 'Mail',
    color: 'from-red-500 to-rose-600',
    parameters: {
      required: ['to', 'subject'],
      optional: ['cc', 'bcc', 'body_text', 'body_html'],
      schema: {
        to: {
          type: 'text',
          label: 'To',
          placeholder: 'recipient@example.com (comma-separated for multiple)',
          required: true
        },
        subject: {
          type: 'text',
          label: 'Subject',
          placeholder: 'Email subject',
          required: true
        },
        cc: {
          type: 'text',
          label: 'CC',
          placeholder: 'cc@example.com'
        },
        bcc: {
          type: 'text',
          label: 'BCC',
          placeholder: 'bcc@example.com'
        },
        body_text: {
          type: 'textarea',
          label: 'Body (Text)',
          placeholder: 'Plain text email body',
          rows: 4
        },
        body_html: {
          type: 'textarea',
          label: 'Body (HTML)',
          placeholder: '<h1>HTML email body</h1>',
          rows: 4
        }
      },
      advanced: {
        integration_id: {
          type: 'integration_select',
          label: 'Gmail Integration',
          integration_type: 'google',
          required: false,
          description: 'Select the Gmail account to send from'
        },
        reply_to: {
          type: 'text',
          label: 'Reply-To',
          placeholder: 'reply@example.com',
          description: 'Email address for replies'
        },
        template_id: {
          type: 'text',
          label: 'Template ID',
          placeholder: 'Email template UUID',
          description: 'Use a pre-built email template'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'gmail_send',
      integration_id: params.integration_id || null,
      to: params.to.split(',').map(e => e.trim()),
      subject: params.subject,
      cc: params.cc ? params.cc.split(',').map(e => e.trim()) : null,
      bcc: params.bcc ? params.bcc.split(',').map(e => e.trim()) : null,
      body_text: params.body_text || null,
      body_html: params.body_html || null,
      reply_to: params.reply_to || null,
      template_id: params.template_id || null
    })
  },

  // ============================================================================
  // DATA PROCESSING
  // ============================================================================
  data_transform: {
    type: 'data_transform',
    category: 'data',
    label: 'Data Transform',
    description: 'Transform and manipulate data',
    icon: 'Shuffle',
    color: 'from-teal-500 to-cyan-600',
    parameters: {
      required: ['transformations'],
      optional: [],
      schema: {
        transformations: {
          type: 'json',
          label: 'Transformations',
          placeholder: `[
  {"action": "rename", "source": "old_field", "target": "new_field"},
  {"action": "format", "source": "name", "target": "name", "format_type": "uppercase"}
]`,
          required: true,
          rows: 6
        }
      }
    },
    generateStepParams: (params) => {
      let transformations = null;
      try {
        transformations = JSON.parse(params.transformations);
      } catch (e) {}
      return {
        step_type: 'data_transform',
        transformations
      };
    }
  },

  database_query: {
    type: 'database_query',
    category: 'data',
    label: 'Database Query',
    description: 'Execute a database query',
    icon: 'Database',
    color: 'from-emerald-500 to-teal-600',
    parameters: {
      required: ['connection_id', 'query'],
      optional: ['query_type', 'timeout', 'parameters'],
      schema: {
        connection_id: {
          type: 'text',
          label: 'Connection ID',
          placeholder: 'Database connection UUID',
          required: true
        },
        query: {
          type: 'textarea',
          label: 'SQL Query',
          placeholder: 'SELECT * FROM orders WHERE id = :order_id',
          required: true,
          rows: 4
        },
        query_type: {
          type: 'select',
          label: 'Query Type',
          options: [
            { value: 'select', label: 'SELECT' },
            { value: 'insert', label: 'INSERT' },
            { value: 'update', label: 'UPDATE' },
            { value: 'delete', label: 'DELETE' }
          ],
          default: 'select'
        },
        timeout: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 1,
          max: 300,
          default: 30
        },
        parameters: {
          type: 'json',
          label: 'Query Parameters',
          placeholder: '{"order_id": "{{input:webhook_payload.id}}"}',
          rows: 3
        }
      },
      advanced: {
        connection_mode: {
          type: 'select',
          label: 'Connection Mode',
          options: [
            { value: 'external', label: 'External (credential_id)' },
            { value: 'zenit', label: 'Zenit Internal (table_id)' }
          ],
          default: 'external',
          description: 'How to connect: external DB via credential, or Zenit internal table'
        },
        database_type: {
          type: 'text',
          label: 'Database Type',
          placeholder: 'postgresql',
          description: 'Database engine type (for external connections)'
        },
        table_id: {
          type: 'text',
          label: 'Table ID',
          placeholder: 'Zenit table UUID',
          description: 'Required when using Zenit connection mode'
        }
      }
    },
    generateStepParams: (params) => {
      let query_params = null;
      try {
        query_params = params.parameters ? JSON.parse(params.parameters) : null;
      } catch (e) {}
      return {
        step_type: 'database_query',
        connection_mode: params.connection_mode || 'external',
        credential_id: params.connection_id || null,
        database_type: params.database_type || null,
        table_id: params.table_id || null,
        query: params.query,
        timeout: params.timeout ?? 30,
        query_params
      };
    }
  },

  // ============================================================================
  // CONTROL FLOW
  // ============================================================================
  condition: {
    type: 'condition',
    category: 'control',
    label: 'Condition (If/Else)',
    description: 'Branch based on conditions with multiple operators',
    icon: 'GitBranch',
    color: 'from-pink-500 to-rose-600',
    parameters: {
      required: ['conditions'],
      optional: ['logic', 'true_branch_action', 'false_branch_action', 'if_description', 'else_description'],
      schema: {
        conditions: {
          type: 'array',
          label: 'Conditions',
          itemSchema: {
            variable: {
              type: 'text',
              label: 'Variable',
              placeholder: '{{context:step_name.result}}',
              required: true
            },
            operator: {
              type: 'select',
              label: 'Operator',
              options: [
                { value: 'equals', label: 'Equals (==)' },
                { value: 'not_equals', label: 'Not Equals (!=)' },
                { value: 'contains', label: 'Contains' },
                { value: 'not_contains', label: 'Does Not Contain' },
                { value: 'starts_with', label: 'Starts With' },
                { value: 'ends_with', label: 'Ends With' },
                { value: 'greater_than', label: 'Greater Than (>)' },
                { value: 'less_than', label: 'Less Than (<)' },
                { value: 'greater_or_equal', label: 'Greater or Equal (>=)' },
                { value: 'less_or_equal', label: 'Less or Equal (<=)' },
                { value: 'is_empty', label: 'Is Empty' },
                { value: 'is_not_empty', label: 'Is Not Empty' }
              ],
              default: 'equals',
              required: true
            },
            value: {
              type: 'text',
              label: 'Value',
              placeholder: 'Expected value',
              required: false
            }
          },
          minItems: 1,
          addButtonLabel: '+ Add Condition'
        },
        logic: {
          type: 'select',
          label: 'Logic',
          options: [
            { value: 'AND', label: 'AND - All conditions must be true' },
            { value: 'OR', label: 'OR - At least one condition must be true' }
          ],
          default: 'AND',
          description: 'How to combine multiple conditions'
        },
        if_description: {
          type: 'text',
          label: 'If True - Description',
          placeholder: 'Describe what happens when condition is true'
        },
        else_description: {
          type: 'text',
          label: 'If False - Description',
          placeholder: 'Describe what happens when condition is false'
        },
        true_branch_action: {
          type: 'select',
          label: 'If True - Action',
          options: [
            { value: 'continue', label: 'Continue to next step' },
            { value: 'skip_to_step', label: 'Skip to specific step' },
            { value: 'complete', label: 'Complete workflow' }
          ],
          default: 'continue'
        },
        false_branch_action: {
          type: 'select',
          label: 'If False - Action',
          options: [
            { value: 'continue', label: 'Continue to next step' },
            { value: 'skip_to_step', label: 'Skip to specific step' },
            { value: 'fail', label: 'Fail workflow' },
            { value: 'complete', label: 'Complete workflow' }
          ],
          default: 'continue'
        }
      },
      advanced: {
        max_iterations: {
          type: 'number',
          label: 'Max Iterations',
          min: 1,
          max: 100,
          default: 10,
          description: 'Maximum loop iterations before triggering on_max_iterations action'
        },
        on_max_iterations: {
          type: 'select',
          label: 'On Max Iterations',
          options: [
            { value: 'fail', label: 'Fail workflow' },
            { value: 'continue', label: 'Continue to next step' }
          ],
          default: 'fail',
          description: 'Action when max iterations is reached'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'condition',
      condition: {
        conditions: params.conditions || [],
        logic: params.logic || 'AND'
      },
      if_description: params.if_description || '',
      else_description: params.else_description || '',
      true_branch_action: params.true_branch_action || 'continue',
      false_branch_action: params.false_branch_action || 'continue',
      true_branch_target_step: null,
      false_branch_target_step: null
    })
  },

  delay: {
    type: 'delay',
    category: 'control',
    label: 'Delay',
    description: 'Wait for a specified duration',
    icon: 'Timer',
    color: 'from-amber-500 to-orange-600',
    parameters: {
      required: ['duration_seconds'],
      optional: [],
      schema: {
        duration_seconds: {
          type: 'number',
          label: 'Duration (seconds)',
          min: 1,
          max: 86400,
          default: 60,
          required: true
        }
      },
      advanced: {
        timeout_seconds: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 1,
          max: 86400,
          description: 'Max wait time before taking the timeout action'
        },
        timeout_action: {
          type: 'select',
          label: 'Timeout Action',
          options: [
            { value: 'fail', label: 'Fail workflow' },
            { value: 'continue', label: 'Continue to next step' }
          ],
          default: 'fail',
          description: 'What to do if the delay times out'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'delay',
      duration_seconds: params.duration_seconds,
      timeout_seconds: params.timeout_seconds || undefined,
      timeout_action: params.timeout_action || undefined
    })
  },

  wait: {
    type: 'wait',
    category: 'control',
    label: 'Wait',
    description: 'Wait for condition, webhook, or user input',
    icon: 'Hourglass',
    color: 'from-yellow-500 to-amber-600',
    parameters: {
      required: ['wait_type'],
      optional: ['timeout_seconds', 'timeout_action'],
      schema: {
        wait_type: {
          type: 'select',
          label: 'Wait Type',
          options: [
            { value: 'duration', label: 'Duration' },
            { value: 'condition', label: 'Condition' },
            { value: 'webhook', label: 'Webhook' },
            { value: 'user_input', label: 'User Input' }
          ],
          required: true,
          default: 'duration'
        },
        timeout_seconds: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 1,
          max: 86400,
          default: 300
        },
        timeout_action: {
          type: 'select',
          label: 'Timeout Action',
          options: [
            { value: 'fail', label: 'Fail' },
            { value: 'continue', label: 'Continue' },
            { value: 'retry', label: 'Retry' }
          ],
          default: 'fail'
        }
      },
      advanced: {
        duration_seconds: {
          type: 'number',
          label: 'Duration (seconds)',
          min: 1,
          max: 86400,
          default: 60,
          description: 'Required when wait_type is "Duration"'
        },
        condition: {
          type: 'textarea',
          label: 'Condition Expression',
          placeholder: '{{context:step_name.result}} == "approved"',
          rows: 2,
          description: 'Required when wait_type is "Condition" — template expression that must evaluate to true'
        },
        webhook_path: {
          type: 'text',
          label: 'Webhook Path',
          placeholder: '/wait/my-webhook',
          description: 'Required when wait_type is "Webhook" — path that resumes the workflow'
        },
        webhook_method: {
          type: 'select',
          label: 'Webhook HTTP Method',
          options: [
            { value: 'POST', label: 'POST' },
            { value: 'GET', label: 'GET' }
          ],
          default: 'POST',
          description: 'HTTP method for the webhook endpoint'
        },
        match_type: {
          type: 'select',
          label: 'Match Type',
          options: [
            { value: 'exact', label: 'Exact' },
            { value: 'contains', label: 'Contains' },
            { value: 'regex', label: 'Regex' },
            { value: 'exists', label: 'Exists' }
          ],
          default: 'exact',
          description: 'How to match the webhook payload field'
        },
        match_field: {
          type: 'text',
          label: 'Match Field',
          placeholder: 'status',
          description: 'Field path in webhook payload to match against'
        },
        match_value_template: {
          type: 'text',
          label: 'Match Value',
          placeholder: 'approved',
          description: 'Expected value in the match field'
        },
        response_mapping: {
          type: 'json',
          label: 'Response Mapping',
          placeholder: '{"output_key": "{{payload.field}}"}',
          rows: 3,
          description: 'Map webhook payload fields to step output variables'
        },
        resume_data_key: {
          type: 'text',
          label: 'Resume Data Key',
          placeholder: 'webhook_data',
          description: 'Key under which the webhook payload is stored in step output'
        },
        wait_for_more_messages_seconds: {
          type: 'number',
          label: 'Wait for More Messages (s)',
          min: 0,
          default: 0,
          description: 'Extra seconds to collect additional webhook calls after first match'
        }
      }
    },
    generateStepParams: (params) => {
      const base = {
        step_type: 'wait',
        wait_type: params.wait_type,
        timeout_seconds: params.timeout_seconds ?? 300,
        timeout_action: params.timeout_action || 'fail'
      };

      if (params.wait_type === 'duration' && params.duration_seconds) {
        base.duration_seconds = parseInt(params.duration_seconds);
      }
      if (params.wait_type === 'condition' && params.condition) {
        base.condition = params.condition;
      }
      if (params.wait_type === 'webhook') {
        if (params.webhook_path) base.webhook_path = params.webhook_path;
        if (params.webhook_method) base.webhook_method = params.webhook_method;
        if (params.match_type) base.match_type = params.match_type;
        if (params.match_field) base.match_field = params.match_field;
        if (params.match_value_template) base.match_value_template = params.match_value_template;
        if (params.response_mapping) {
          try { base.response_mapping = typeof params.response_mapping === 'string' ? JSON.parse(params.response_mapping) : params.response_mapping; } catch (e) {}
        }
        if (params.resume_data_key) base.resume_data_key = params.resume_data_key;
        if (params.wait_for_more_messages_seconds != null) base.wait_for_more_messages_seconds = params.wait_for_more_messages_seconds;
      }

      return base;
    }
  },

  error_handler: {
    type: 'error_handler',
    category: 'control',
    label: 'Error Handler',
    description: 'Handle errors in the workflow',
    icon: 'AlertTriangle',
    color: 'from-red-500 to-rose-600',
    parameters: {
      required: ['error_types', 'action'],
      optional: ['retry_count'],
      schema: {
        error_types: {
          type: 'text',
          label: 'Error Types',
          placeholder: 'timeout,api_error,validation_error',
          required: true
        },
        action: {
          type: 'select',
          label: 'Action',
          options: [
            { value: 'retry', label: 'Retry' },
            { value: 'skip', label: 'Skip' },
            { value: 'fail', label: 'Fail' },
            { value: 'custom', label: 'Custom Handler' }
          ],
          required: true,
          default: 'retry'
        },
        retry_count: {
          type: 'number',
          label: 'Retry Count',
          min: 1,
          max: 10,
          default: 3
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'error_handler',
      error_types: params.error_types.split(',').map(e => e.trim()),
      action: params.action,
      retry_count: params.action === 'retry' ? (params.retry_count ?? 3) : null
    })
  },

  wait_for_human_action: {
    type: 'wait_for_human_action',
    category: 'control',
    label: 'Wait for Human Action',
    description: 'Send an email and pause until a human picks one of the configured actions',
    icon: 'UserCheck',
    color: 'from-orange-500 to-amber-600',
    parameters: {
      required: ['integration_id', 'recipient', 'subject', 'message', 'actions'],
      optional: ['timeout_seconds', 'timeout_action', 'language'],
      schema: {
        integration_id: {
          type: 'integration_select',
          label: 'Email Integration (Gmail)',
          integration_type: 'google',
          required: true,
          description: 'Select the Gmail account used to send the notification'
        },
        recipient: {
          type: 'text',
          label: 'Recipient Email',
          placeholder: 'approver@example.com or {{trigger:contact_email}}',
          required: true,
          description: 'Email of the human who must act. Supports templates.'
        },
        subject: {
          type: 'text',
          label: 'Subject',
          placeholder: 'Approval required for order {{context:step_name.order_id}}',
          required: true
        },
        message: {
          type: 'textarea',
          label: 'Message',
          placeholder: 'Please review and take action on this request.\n\n**Order:** {{context:step_name.order_id}}',
          required: true,
          rows: 4,
          description: 'Supports templates and basic Markdown (bold, italic, lists).'
        },
        actions: {
          type: 'array',
          label: 'Actions',
          itemLabel: 'Action',
          required: true,
          itemSchema: {
            id: {
              type: 'text',
              label: 'Action ID',
              placeholder: 'approved',
              required: true
            },
            label: {
              type: 'text',
              label: 'Button Label',
              placeholder: 'Approve',
              required: true
            }
          },
          minItems: 1,
          addButtonLabel: '+ Add Action',
          description: 'Buttons shown on the decision page. At least 1, at most 10.'
        },
        timeout_seconds: {
          type: 'number',
          label: 'Timeout (seconds)',
          min: 60,
          max: 2592000,
          default: 86400,
          description: 'Max wait time. Default 24h (86400). Leave empty to use default.'
        },
        timeout_action: {
          type: 'select',
          label: 'Timeout Action',
          options: [
            { value: 'fail', label: 'Fail — workflow fails if nobody responds' },
            { value: 'continue', label: 'Continue — resumes with action_id="timeout"' }
          ],
          default: 'fail',
          description: 'What to do if nobody responds before the timeout.'
        },
        language: {
          type: 'select',
          label: 'Email Language',
          options: [
            { value: 'en', label: 'English' },
            { value: 'es', label: 'Spanish' },
            { value: 'fr', label: 'French' },
            { value: 'pt', label: 'Portuguese' },
            { value: 'de', label: 'German' }
          ],
          default: 'en',
          description: 'Language for the email chrome (footer, labels). Your subject/message are sent as-is.'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'wait_for_human_action',
      wait_type: 'human_action',
      version: 1,
      channel: 'email',
      integration_id: params.integration_id,
      language: params.language || 'en',
      recipient: params.recipient,
      subject: params.subject,
      message: params.message,
      actions: params.actions || [],
      timeout_seconds: params.timeout_seconds ?? 86400,
      timeout_action: params.timeout_action || 'fail'
    })
  },

  execute_workflow: {
    type: 'execute_workflow',
    category: 'control',
    label: 'Execute Workflow',
    description: 'Call another workflow in the same account',
    icon: 'Shuffle',
    color: 'from-indigo-500 to-violet-600',
    parameters: {
      required: ['workflow_id'],
      optional: ['inputs', 'wait_for_completion', 'continue_on_failure', 'timeout_seconds'],
      schema: {
        workflow_id: {
          type: 'text',
          label: 'Target Workflow ID',
          placeholder: 'UUID of the target workflow',
          required: true
        },
        wait_for_completion: {
          type: 'select',
          label: 'Execution Mode',
          default: 'true',
          options: [
            { value: 'true', label: 'Sync — wait for completion' },
            { value: 'false', label: 'Async — fire and forget' },
          ]
        },
        timeout_seconds: {
          type: 'number',
          label: 'Timeout (seconds)',
          default: 300,
          min: 1,
          placeholder: '300'
        },
        continue_on_failure: {
          type: 'select',
          label: 'Continue on Failure',
          default: 'false',
          options: [
            { value: 'false', label: 'No — fail this workflow too' },
            { value: 'true', label: 'Yes — continue even if child fails' },
          ]
        },
        inputs: {
          type: 'json',
          label: 'Input Data',
          placeholder: '{"order_id": "{{input:webhook_payload.id}}"}',
          rows: 3,
          description: 'Key-value pairs passed as input to the child workflow'
        }
      }
    },
    generateStepParams: (params) => {
      let inputs = {};
      try { inputs = params.inputs ? JSON.parse(params.inputs) : {}; } catch (e) {}
      return {
        step_type: 'execute_workflow',
        version: 1,
        workflow_id: params.workflow_id,
        inputs,
        wait_for_completion: params.wait_for_completion !== 'false',
        continue_on_failure: params.continue_on_failure === 'true',
        timeout_seconds: params.timeout_seconds ? parseInt(params.timeout_seconds) : 300
      };
    }
  },

  // ============================================================================
  // SYSTEM
  // ============================================================================
  log: {
    type: 'log',
    category: 'system',
    label: 'Log',
    description: 'Log a message',
    icon: 'FileText',
    color: 'from-gray-500 to-slate-600',
    parameters: {
      required: ['message'],
      optional: ['level', 'logger_name'],
      schema: {
        message: {
          type: 'textarea',
          label: 'Message',
          placeholder: 'Order {{input:webhook_payload.id}} processed successfully',
          required: true,
          rows: 2
        },
        level: {
          type: 'select',
          label: 'Log Level',
          options: [
            { value: 'debug', label: 'Debug' },
            { value: 'info', label: 'Info' },
            { value: 'warning', label: 'Warning' },
            { value: 'error', label: 'Error' },
            { value: 'critical', label: 'Critical' }
          ],
          default: 'info'
        },
        logger_name: {
          type: 'text',
          label: 'Logger Name',
          placeholder: 'Optional custom logger name'
        }
      }
    },
    generateStepParams: (params) => ({
      step_type: 'log',
      message: params.message,
      level: params.level || 'info',
      logger_name: params.logger_name || null
    })
  }
};

/**
 * Helper para crear un nuevo nodo con ID temporal
 */
export const createNodeInstance = (nodeType, position = { x: 0, y: 0 }) => {
  const definition = NODE_DEFINITIONS[nodeType];
  if (!definition) {
    throw new Error(`Unknown node type: ${nodeType}`);
  }

  const tempId = generateUUID();
  
  return {
    id: tempId,
    type: nodeType,
    position,
    data: {
      ...definition,
      nodeId: tempId,
      params: {},
      isConfigured: false
    }
  };
};

/**
 * Helper para obtener nodos por categoría
 */
export const getNodesByCategory = (category) => {
  return Object.entries(NODE_DEFINITIONS)
    .filter(([_, def]) => def.category === category)
    .map(([type, def]) => ({ type, ...def }));
};

/**
 * Exportar lista de todos los tipos de nodos
 */
export const getAllNodeTypes = () => {
  return Object.keys(NODE_DEFINITIONS);
};
